"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { Icon } from "@repo/ui/icon";
import { useScrollLock } from "../../lib/useScrollLock";

type MenuOverlayProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Fullscreen overlay menu (reusable)
 * - Keeps your original markup & animations
 * - Accessible: role="dialog", aria-modal, Esc to close, focus handling, scroll lock
 */
export default function MenuOverlay({ open, onClose }: MenuOverlayProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const leftListRef = useRef<HTMLUListElement>(null);
  const rightColRef = useRef<HTMLDivElement>(null);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  const { restoreScroll } = useScrollLock(open);

  // Handle focus and keyboard events
  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();

    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Add/remove class to body when menu is open/closed
  useEffect(() => {
    if (open) {
      // Prevent scroll and add class
      document.body.style.overflow = "hidden";
      document.body.classList.add("menu-open");
    } else {
      // Restore scroll and remove class
      document.body.style.overflow = "";
      document.body.classList.remove("menu-open");
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
      document.body.classList.remove("menu-open");
    };
  }, [open]);

  // Cleanup scroll lock on unmount
  useEffect(() => {
    return () => {
      restoreScroll();
    };
  }, [restoreScroll]);

  // GSAP entrance animation
  useEffect(() => {
    if (!open) return;

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      gsap.set(panelRef.current, { opacity: 1 });
      return;
    }

    const ctx = gsap.context(() => {
      const panel = panelRef.current;
      const right = rightColRef.current;
      const leftItems =
        leftListRef.current?.querySelectorAll<HTMLLIElement>("li") ?? [];

      // Set initial state - panel visible immediately to prevent glitch
      gsap.set(panel, { opacity: 1, scale: 1 });
      gsap.set(leftItems, { y: 10, opacity: 0 });
      gsap.set(right, { x: 20, opacity: 0 });

      const tl = gsap
        .timeline({ defaults: { ease: "power2.out" } })
        .to(leftItems, { opacity: 1, y: 0, duration: 0.25, stagger: 0.04 }, "0")
        .to(right, { opacity: 1, x: 0, duration: 0.2 }, "0.05");

      tlRef.current = tl;
    }, panelRef);

    return () => {
      ctx.revert();
      tlRef.current = null;
    };
  }, [open]);

  const closeInstant = () => {
    // Force restore scroll before closing
    restoreScroll();
    onClose();
  };

  return (
    <div
      id="preintro-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Hotel quick menu"
      tabIndex={-1}
      ref={panelRef}
      className={`fixed top-0 left-0 w-screen h-screen bg-white overflow-y-auto z-[10000] ${open ? "flex" : "hidden"} md:flex-row flex-col md:pt-0 pt-10 justify-between`}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close menu"
        className="absolute cursor-pointer right-4 top-4 p-2 z-[10001]"
      >
        <Icon name="x" size={28} />
      </button>

      <div className="md:w-1/2 self-stretch flex md:flex-nowrap flex-wrap md:pb-0 pb-6 justify-center items-center pr-10">
        <ul ref={leftListRef} className="flex flex-col gap-4">
          <li>
            <Link
              href="/rooms/"
              className="inline-block hover:bg-primary-hover hover:text-white xl:text-2xl md:text-xl text-base xl:py-6 md:py-4 py-3 uppercase border border-primary xl:px-9 md:px-7 px-5"
              onClick={closeInstant}
            >
              rooms & suites
            </Link>
          </li>
          <li>
            <Link
              href="/#your-swiss-escape"
              className="inline-block hover:bg-primary-hover hover:text-white xl:text-2xl md:text-xl text-base xl:py-6 md:py-4 py-3 uppercase border border-primary xl:px-9 md:px-7 px-5"
              onClick={closeInstant}
            >
              Activities
            </Link>
          </li>
          <li>
            <Link
              href="/#elevated-experiences-at-lucerne"
              className="inline-block hover:bg-primary-hover transition hover:text-white xl:text-2xl md:text-xl text-base xl:py-6 md:py-4 py-3 uppercase border border-primary xl:px-9 md:px-7 px-5"
              onClick={closeInstant}
            >
              Highlights
            </Link>
          </li>
        </ul>
      </div>

      <div
        ref={rightColRef}
        className="md:w-1/2 w-full bg-[#FAFAFA] self-stretch flex flex-col justify-center items-center gap-8"
      >
        <ul className="flex flex-col gap-1.5 xl:w-1/2 w-full border-b border-gray-200 md:pb-8 pb-4 xl:px-0 px-10 md:pt-0 pt-4">
          <li>
            <a
              href="https://lmsgroup.ch/about/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeInstant}
              className="flex items-center gap-2 hover:underline"
            >
              About us
              <Icon size={14} name="arrow-up-right" />
            </a>
          </li>
          <li>
            <a
              href="https://lmsgroup.ch/careers/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeInstant}
              className="flex items-center gap-2 hover:underline"
            >
              Careers
              <Icon size={14} name="arrow-up-right" />
            </a>
          </li>
        </ul>

        <ul className="tracking-tight xl:w-1/2 w-full flex flex-col gap-2 md:gap-5 2xl:min-w-96 pr-10 border-b border-gray-200 pb-8 xl:px-0 px-10">
          <li>
            <span className="font-semibold">LMS Lucerne</span>
          </li>
          <li>
            <span className="uppercase text-sm text-gray-600">
              Bodenhof 3, 6014 Littau, Switzerland
            </span>
          </li>
        </ul>

        <div className="flex flex-col gap-5 md:flex-row xl:w-1/2 w-full border-b border-gray-200 pb-8 xl:px-0 px-10 justify-between">
          <span>Room reservation</span>
          <div className="flex flex-col gap-3">
            <Link
              href="tel:+41565447800"
              className="uppercase min-w-48 py-4 px-6 uppercase border border-primary  text-center hover:text-white hover:bg-primary-hover transition"
              onClick={closeInstant}
            >
              +41 56 544 78 00
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-5 md:flex-row xl:w-1/2 w-full border-b border-gray-200 md:pb-8 xl:px-0 px-10 pb-14 justify-between">
          <span>Restaurant reservation</span>
          <div className="flex flex-col gap-3">
            <Link
              href="tel:+41565447800"
              className="uppercase min-w-48 py-4 uppercase border border-primary text-center hover:bg-primary-hover hover:text-white transition"
              onClick={closeInstant}
            >
              +41 56 544 78 00
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
